import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NzNotificationService, NzNotificationServiceModule } from 'ng-zorro-antd/notification';
import { NzDrawerModule, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { OrdersFacade } from '../../../+store/order/order.facade';
import {
  DrawerParms,
  DrawerType,
  IAdminTokenPayload,
  ICustomerUser,
  ILabReq,
  IOrder,
  IOrderRequirement,
  IOrgUser,
  IProviderPrescribingContext,
  OrderDirection,
  OrderStatusKey,
  Prescription,
  IEMRLayout,
  OrderPrescriptionType,
  ISubscription,
  IPharmacyAddress,
  IFailedWorkflowRunSummary,
  IPharmacy,
  IWorkflowRunError,
  resolveOrderChargeAmount,
  resolveDeferredAmountAfterTotalChange,
  LabReqTemplate
} from '@care-portals/api-interfaces';
import { CommonModule } from '@angular/common';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { DrawerStateFacade } from '../../../+store/drawer-state/drawer-state.facade';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { AdminUserFacade } from '../../../+store/admin-user/admin-user.facade';
import { map, takeWhile } from 'rxjs';
import {
  ORDER_STATUSES,
  QUESTIONNAIRE_STATUSES,
  PAYMENT_STATUSES,
  labReqTemplates,
  VERIFICATION_REQS
} from '@care-portals/data';
import { cleanErrorText } from '@care-portals/ui';
import { PaymentStatusKey } from '@care-portals/api-interfaces';
import { NotesModule } from '../../../modules/notes.module';
import { environment } from '../../../../environments/environment';
import { VariantSelectComponent } from '../variant-select/variant-select.component';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalService, NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { OrderedShippedDialogComponent } from '../ordered-shipped-dialog/ordered-shipped-dialog.component';
import { getTrackingLink } from '@care-portals/shared';
import { getAvailableRequirements } from '@care-portals/shared';
import { ConsultationFacade } from '../../../+store/consultation/consultation.facade';
import { OrderPrescriptionsComponent } from '../order-prescriptions/order-prescriptions.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { UserDetailsPipe } from '../../../pipes/user-details.pipe';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { CustomerSummaryComponent } from '../../customer/customer-summary/customer-summary.component';
import { AddressDisplayComponent } from '../../common/address-display/address-display.component';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { AppFacade } from '../../../+store/app/app.facade';
import { CustomerUnfulfilledOrdersComponent } from '../customer-unfulfilled-orders/customer-unfulfilled-orders.component';
import { DosespotService } from '../../../services/dosespot/dosespot.service';
import { SubscriptionsFacade } from '../../../+store/subscriptions/subscriptions.facade';
import { OrderEditorComponent } from '../order-editor/order-editor.component';
import { PaymentStatusModalComponent } from '../payment-status-modal/payment-status-modal.component';
import { OrderedRejectedDialogComponent } from '../ordered-rejected-dailog/ordered-rejected-dialog.component';
import { OrderedCancelledDialogComponent } from '../ordered-cancelled-dailog/ordered-cancelled-dialog.component';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { LabResultsFacade } from 'src/app/+store/lab-results/lab-results.facade';
import { Actions, ofType } from '@ngrx/effects';
import { LabReqFacade } from '../../../+store/lab-req/lab-req.facade';
import * as LabReqActions from '../../../+store/lab-req/lab-req.actions';
import * as CustomerActions from '../../../+store/customer/customer.actions';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { PrescriptionFacade } from '../../../+store/prescription/prescription.facade';
import { WorkflowRunDetailDrawerComponent } from '../workflow-run-detail-drawer/workflow-run-detail-drawer.component';
import { OrderVisitComponent } from '../order-visit/order-visit.component';
import { APIService } from '../../../services/api/api.service';

@Component({
  selector: 'portals-emr-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    VariantSelectComponent,
    NzNotificationServiceModule,
    NzLayoutModule,
    NzSelectModule,
    NzTableModule,
    NzButtonModule,
    NzSpinModule,
    NzTagModule,
    NzInputModule,
    NzInputNumberModule,
    NotesModule,
    NzFormModule,
    NzDividerModule,
    NzTimelineModule,
    OrderPrescriptionsComponent,
    NzToolTipModule,
    UserDetailsPipe,
    NzIconModule,
    NzDescriptionsModule,
    CustomerSummaryComponent,
    AddressDisplayComponent,
    CustomerUnfulfilledOrdersComponent,
    NzModalModule,
    NzPopconfirmModule,
    OrderEditorComponent,
    PaymentStatusModalComponent,
    ClipboardModule,
    NzDrawerModule,
    WorkflowRunDetailDrawerComponent,
    OrderVisitComponent
  ]
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  @Input() orderId: number;
  order: IOrder & {
    rxResults?: {
      prescriptions: Prescription[];
      type: OrderPrescriptionType;
    };
    failedWorkflowRuns?: IFailedWorkflowRunSummary[];
  };
  selectedLayout: IEMRLayout;
  isLoading = false;
  error;
  active = true;
  requirements: IOrderRequirement[];
  noteInput = '';
  assignedToProvider = '';
  assignedToGroup = '';
  assignedTo = '';
  assignedPharmacy = '';
  adminUsersOptions: IOrgUser[] = [];
  pharmaciesOptions: IPharmacy[] = [];
  groupsOptions: string[] = [];
  statusOptions = Object.entries(ORDER_STATUSES)
    .filter(s => s[1].show)
    .sort((a, b) => (a[1].sort < b[1].sort ? -1 : 1));
  view_order_url = environment.bigcommerce_view_order_url;
  isShippingAddressEditMode = false;
  isExtrasEditMode = false;
  isRequirementsEditMode = false;
  extrasUpdates = [];
  customerLink = '';
  orderStatuses = {};
  prescriptionsLoaded = false;
  prescriptionsLoadedType: OrderPrescriptionType;
  qnrStatusNames = QUESTIONNAIRE_STATUSES;
  paymentStatuses = PAYMENT_STATUSES;
  isSaving$ = this.orderFacade.savingOrder$;
  availableQs = [];
  selectedReq;
  additionalReqs = [];
  orderVersion: number;
  showUnfulfilledOrders = true;
  activeDigitalSubscriptions: ISubscription[] = [];
  activePhysicalSubscriptions: ISubscription[] = [];
  isProductsEditMode = false;
  showTotalAmountModal = false;
  formValidity = true;
  showPaymentStatusModal = false;
  retriggerStatusAvailable = true;
  showLinkPrescriptionModal = false;
  enableTransferOutAfterLink = false;
  labReqTemplates = labReqTemplates;
  selectedLabReqTemplate: LabReqTemplate | null = null;
  orderLabReqs$ = this.labReqFacade.allLabReq$.pipe(
    map(reqs => [...reqs].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ))
  );
  labReqLoading$ = this.labReqFacade.loading$;
  showAllLabReqs = false;
  showMarkCompletedModal = false;
  justCreatedLabReqId: string | null = null;
  /** "Failed Workflow Runs" list drawer (the group of same-minute failures). */
  failedRunsDrawerVisible = false;
  selectedFailedRuns: IFailedWorkflowRunSummary[] = [];
  selectedFailedRunsTime: string | Date | null = null;
  workflowRunDetailDrawerVisible = false;
  workflowRunDetailId: string | null = null;
  /** Runs reran this session - flagged blue in history without waiting for reload. */
  private reranRunIds = new Set<string>();
  selectedPrescriptionToLinkId: string | null = null;
  isPharmacyAddressEditMode = false;
  /** Set when `order` changes; not a getter — avoids new array refs on every CD with *ngFor. */
  filteredOrderExtrasEntries: { key: string; value: unknown }[] = [];
  /** Merged status + failed-run timeline; synced when order changes (not a getter). */
  statusTimelineEntries: Array<
    | { kind: 'status'; time: string | Date; newStatus: OrderStatusKey; userId: string }
    | {
      kind: 'failed';
      time: string | Date;
      runs: IFailedWorkflowRunSummary[];
      reran: boolean;
    }
  > = [];
  /** Products table rows; synced when order changes (not a getter). */
  productTableRows: {
    lineItem: NonNullable<IOrder['lineItems']>[number];
    prescribingContext: IProviderPrescribingContext | null;
  }[] = [];
  get pharmacyAddress(): IPharmacyAddress | null {
    return (
      this.order?.pharmacyAddress || this.order?.meta?.pharmacyAddress || null
    );
  }

  get isTransferOut(): boolean {
    return !!this.order?.transferOut;
  }

  get paymentStatus(): PaymentStatusKey | null {
    if (!this.order) return null;

    // order.payment object is new and did not apply to all successful payments
    // so rely on order.meta.paymentStatus for this instance
    // Can be removed later on
    if (this.order?.payment?.status) {
      return this.order.payment.status;
    } else if (this.order.meta?.paymentStatus === 'succeeded') {
      return 'paid';
    } else {
      return null;
    }
  }

  get paymentDetails() {
    if (!this.order) return null;

    // Get payment data from order
    const payment = this.order.payment || null;

    if (!payment) return null;

    return {
      status: payment.status as PaymentStatusKey,
      amount: payment.amount,
      refundAmount: payment.refundAmount,
      failedReason: payment.failedReason,
      paidAt: payment.paidAt,
      gateway: payment.gateway,
      paymentId: payment.paymentId
    };
  }

  get paymentStatusColor(): string {
    if (!this.paymentStatus) return '#666';
    return this.paymentStatuses[this.paymentStatus]?.color || '#666';
  }

  get isDelayedCapture(): boolean {
    return !!this.order?.payment?.delayedAmount;
  }

  addressFG = new FormGroup({
    address1: new FormControl('', [Validators.required]),
    address2: new FormControl('', []),
    city: new FormControl('', [Validators.required]),
    provinceCode: new FormControl('', [Validators.required]),
    postalCode: new FormControl('', [Validators.required]),
    countryCode: new FormControl('', [Validators.required])
  });
  pharmacyAddressFG = new FormGroup({
    name: new FormControl(''),
    phoneNumber: new FormControl(''),
    faxNumber: new FormControl('', [Validators.required]),
    address1: new FormControl(''),
    address2: new FormControl(''),
    city: new FormControl(''),
    provinceCode: new FormControl(''),
    postalCode: new FormControl(''),
    countryCode: new FormControl('CA')
  });
  editedOrder: Partial<IOrder>;

  currentUser: IAdminTokenPayload;
  constructor(
    private readonly orderFacade: OrdersFacade,
    private readonly prescriptionFacade: PrescriptionFacade,
    private readonly nzDrawerRef: NzDrawerRef,
    private readonly drawerFacade: DrawerStateFacade,
    private readonly adminUserFacade: AdminUserFacade,
    private readonly consultationFacade: ConsultationFacade,
    private readonly authFacade: AuthFacade,
    private readonly modalService: NzModalService,
    private readonly appFacade: AppFacade,
    private readonly dosespotService: DosespotService,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly clipboard: Clipboard,
    private labResultsFacade: LabResultsFacade,
    private notification: NzNotificationService,
    private readonly apiService: APIService,
    private readonly labReqFacade: LabReqFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly actions$: Actions
  ) {
    Object.keys(ORDER_STATUSES).forEach(key => {
      this.orderStatuses[key] = ORDER_STATUSES[key].name;
    });
  }

  get orderCustomer() {
    return this.order?.customer as Partial<ICustomerUser>;
  }

  get products() {
    return this.order?.lineItems || [];
  }

  get invoices() {
    return this.order?.invoices || [];
  }

  /**
   * Status changes and failed workflow runs merged into a single ascending
   * (oldest-first) timeline, so failures appear inline at the moment they
   * happened rather than lumped together.
   */
  private syncStatusTimelineEntries(): void {
    const statusEntries = (this.order?.statusHistory || []).map(history => ({
      kind: 'status' as const,
      time: history.updatedAt,
      newStatus: history.newStatus,
      userId: history.userId
    }));

    // Group failed runs that fall in the same minute into one node (+N).
    const minuteGroups = new Map<number, IFailedWorkflowRunSummary[]>();
    (this.order?.failedWorkflowRuns || []).forEach(run => {
      const minuteBucket = Math.floor(new Date(run.createdAt).getTime() / 60000);
      const group = minuteGroups.get(minuteBucket) || [];
      group.push(run);
      minuteGroups.set(minuteBucket, group);
    });

    // A node shows blue ("reran") only once every run in it has been reran
    // if any open failure remains it stays red.
    const failedEntries = Array.from(minuteGroups.values()).map(runs => ({
      kind: 'failed' as const,
      time: runs[0].createdAt,
      runs,
      reran: runs.every(run => this.isRunReran(run))
    }));

    this.statusTimelineEntries = [...statusEntries, ...failedEntries].sort(
      (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
    );
  }

  /**
   * Drop focus from the clicked trigger before a drawer opens. ng-zorro's drawer
   * captures the active element on open and re-focuses it on close (without
   * `preventScroll`), which scrolls the trigger back into view and makes the
   * page behind "jump". Blurring first means it restores focus to <body> instead.
   */
  private blurActiveElement(): void {
    const el =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;
    if (el && typeof el.blur === 'function') {
      el.blur();
    }
  }

  openFailedRunsDrawer(runs: IFailedWorkflowRunSummary[], time: string | Date) {
    this.blurActiveElement();
    // A single failure in this node — skip the interim list and open it directly.
    if (runs.length === 1) {
      this.openRunDetail(runs[0]);
      return;
    }
    this.selectedFailedRuns = runs;
    this.selectedFailedRunsTime = time;
    this.failedRunsDrawerVisible = true;
  }

  summarizeRunError(error?: string | IWorkflowRunError): string {
    if (!error) {
      return '';
    }
    if (typeof error === 'object') {
      return cleanErrorText(error.displayMessage ?? error.message);
    }
    let text = error;
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        text = String(parsed.message ?? parsed.details ?? error);
      } else if (typeof parsed === 'string') {
        text = parsed;
      }
    } catch {
      /* not JSON — use the raw string */
    }
    return cleanErrorText(text);
  }

  closeFailedRunsDrawer() {
    this.failedRunsDrawerVisible = false;
    this.selectedFailedRuns = [];
  }

  /**
   * Open the shared Workflow Run Details view for one failed run. The drawer
   * component loads the full run from the CRM API and owns the rerun action —
   * keeping EMR users out of the CRM.
   */
  openRunDetail(run: IFailedWorkflowRunSummary): void {
    if (!run?._id) {
      return;
    }
    this.blurActiveElement();
    this.workflowRunDetailId = run._id;
    this.workflowRunDetailDrawerVisible = true;
  }

  /**
   * A run was reran from the detail drawer. Flag it as reran so the history node
   * turns blue immediately (instead of staying red) without waiting for a reload;
   * on the next load the backend returns `reranAt` and it stays blue.
   */
  onWorkflowRunReran(runId: string): void {
    if (!runId) {
      return;
    }
    this.reranRunIds.add(runId);
    this.selectedFailedRuns = this.selectedFailedRuns.map(run =>
      run._id === runId ? { ...run, reranAt: run.reranAt ?? new Date() } : run
    );
    this.syncStatusTimelineEntries();
  }

  /** A run counts as reran if the backend marked it, or it was reran this session. */
  private isRunReran(run: IFailedWorkflowRunSummary): boolean {
    return !!run.reranAt || this.reranRunIds.has(run._id);
  }

  ngOnInit() {
    if (!this.orderId) {
      this.error = 'order ID is missing';
      return;
    }
    this.isLoading = true;
    this.orderFacade.activeOrder$
      .pipe(takeWhile(() => this.active))
      .subscribe(order => {
        this.order = order;
        this.noteInput = order?.note;
        this.assignedToGroup = order?.assignedToGroup || '';
        this.assignedToProvider =
          typeof order?.assignedTo === 'string'
            ? order?.assignedTo
            : order?.assignedTo?._id || '';
        this.assignedTo =
          typeof order?.assignedTo === 'string'
            ? order?.assignedTo
            : order?.assignedTo?._id || '';
        this.assignedPharmacy =
          typeof order?.assignedPharmacy === 'string'
            ? order?.assignedPharmacy
            : order?.assignedPharmacy?._id || '';
        this.prescriptionsLoaded = !!order?.rxResults?.prescriptions;
        this.prescriptionsLoadedType = order?.rxResults?.type;
        this.failedRunsDrawerVisible = false;
        this.workflowRunDetailDrawerVisible = false;
        this.workflowRunDetailId = null;
        this.orderVersion = order?.version || 1;
        this.selectedPrescriptionToLinkId = null;
        if (order?.id && this.orderId != order.id) {
          this.orderId = order.id;
          this.reranRunIds.clear();
        }

        if (order?._id) {
          // does not work for legacy orders
          this.isLoading = true;
          this.loadOrderRequirements();
        }

        if (order?.customer) {
          const customerId = typeof order.customer === 'string'
            ? order.customer
            : (order.customer as Partial<ICustomerUser>)._id as string;
          this.subscriptionsFacade.getSubscriptions(
            'newer',
            { customer: customerId },
            true,
            true
          );
          if (customerId) {
            this.showAllLabReqs = false;
            this.justCreatedLabReqId = null;
            this.labReqFacade.loadLabReq({ customer: customerId }, 'new');
          }
        }

        this.isLoading = false;
        this.syncOrderExtrasView();
      });

    this.orderFacade.loadOrder(this.orderId);

    this.actions$.pipe(
      ofType(LabReqActions.saveLabReqSuccess),
      takeWhile(() => this.active)
    ).subscribe(({ labReq }) => {
      this.justCreatedLabReqId = labReq._id;
      this.showMarkCompletedModal = true;
    });

    this.actions$.pipe(
      ofType(CustomerActions.updateCustomerSuccess),
      takeWhile(() => this.active)
    ).subscribe(() => {
      this.loadOrderRequirements();
    });

    this.adminUserFacade.adminUsers$
      .pipe(takeWhile(() => this.active))
      .subscribe(users => {
        this.adminUsersOptions.splice(
          0,
          this.adminUsersOptions.length,
          ...users
        );
        this.groupsOptions = [
          ...new Set(users?.map?.(user => user.groups).flat() || [])
        ].filter(Boolean);
      });
    this.adminUserFacade.getAdminUsers();
    this.apiService
      .getPharmacies()
      .pipe(takeWhile(() => this.active))
      .subscribe(pharmacies => {
        this.pharmaciesOptions = pharmacies || [];
      });

    this.authFacade.user$.subscribe(user => {
      this.currentUser = user;
    });

    this.appFacade.selectedLayout$
      .pipe(takeWhile(() => this.active))
      .subscribe(res => {
        this.selectedLayout = res;
        this.loadAvailableRequirements();
      });

    this.subscriptionsFacade.activeDigitalSubscriptions$
      .pipe(takeWhile(() => this.active))
      .subscribe(subscriptions => {
        this.activeDigitalSubscriptions = subscriptions;
      });

    this.subscriptionsFacade.activePhysicalSubscriptions$
      .pipe(takeWhile(() => this.active))
      .subscribe(subscriptions => {
        this.activePhysicalSubscriptions = subscriptions;
      });
  }

  ngOnDestroy(): void {
    this.active = false;
    this.orderFacade.clearActiveOrder();
  }

  loadOrderRequirements() {
    this.orderFacade
      .getOrderRequirements(this.orderId)
      .then(res => {
        this.requirements = res.filter(req => req.source !== 'manual');
        this.additionalReqs = res.filter(req => req.source === 'manual');
        const requirementKeys = [
          ...this.requirements.map(req => req.key),
          ...this.additionalReqs.map(req => req.key)
        ];
        this.availableQs = this.availableQs.filter(
          qKey => !requirementKeys.includes(qKey.key)
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  closeDrawer() {
    this.nzDrawerRef.close();
  }

  get linkedPrescription(): Prescription | null {
    const prescriptions = this.order?.rxResults?.prescriptions || [];
    return (
      prescriptions.find(
        rx => Number(rx?.linkedOrderId) === Number(this.orderId)
      ) || null
    );
  }

  get availablePrescriptionsToLink(): Prescription[] {
    const prescriptions = this.order?.rxResults?.prescriptions || [];
    return prescriptions.filter(
      rx =>
        !rx?.voidedAt &&
        !rx?.transferredOutAt &&
        rx?.linkedOrderId == null
    );
  }

  getPrescriptionLabel(rx: Prescription): string {
    const createdAt = rx?.createdAt ? new Date(rx.createdAt).toLocaleString() : '';
    return `RX ${rx?._id} ${createdAt ? '- ' + createdAt : ''}`.trim();
  }

  viewLinkedPrescription() {
    const rx = this.linkedPrescription;
    if (!rx?._id) {
      this.notification.warning('Prescription not found', 'No linked RX to open.');
      return;
    }

    this.drawerFacade.openDrawer({
      name: DrawerType.PRESCRIPTION_PDF,
      props: { rxId: rx._id }
    });
  }

  transferLinkedPrescription() {
    const rx = this.linkedPrescription;
    if (!rx?._id) {
      this.notification.warning('Prescription not found', 'No linked RX to transfer.');
      return;
    }
    if (rx.transferredOutAt) {
      this.notification.info('Already transferred', `RX ${rx._id} is already transferred out.`);
      return;
    }

    this.modalService.confirm({
      nzTitle: 'Transfer linked RX?',
      nzContent: `RX ${rx._id} will be sent to patient chat and marked transferred out.`,
      nzOkText: 'Transfer RX',
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        this.prescriptionFacade.sendPrescriptionPdfToPatientChat(rx._id, this.orderId);
        return true;
      }
    });
  }

  unlinkLinkedPrescription() {
    const rx = this.linkedPrescription;
    if (!rx?._id) {
      this.notification.warning('Prescription not found', 'No linked RX to unlink.');
      return;
    }

    this.modalService.confirm({
      nzTitle: 'Unlink prescription?',
      nzContent: `RX ${rx._id} will be unlinked from order #${this.orderId}.`,
      nzOkText: 'Unlink',
      nzOkDanger: true,
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        this.prescriptionFacade.updatePrescription(
          rx._id,
          { linkedOrderId: null },
          { orderId: this.orderId, transferOutAfterLink: false }
        );
        return true;
      }
    });
  }

  linkSelectedPrescriptionToOrder() {
    if (!this.selectedPrescriptionToLinkId) {
      this.notification.warning('Select prescription', 'Please choose an RX to link.');
      return;
    }
    const selectedRx = this.availablePrescriptionsToLink.find(
      rx => rx._id === this.selectedPrescriptionToLinkId
    );
    if (!selectedRx?._id) {
      this.notification.warning(
        'Prescription not found',
        'The selected RX is not available to link.'
      );
      return;
    }

    const orderId = this.orderId;
    const rxId = selectedRx._id;
    const transferOutAfterLink = this.enableTransferOutAfterLink;

    this.prescriptionFacade.updatePrescription(
      rxId,
      { linkedOrderId: orderId.toString() },
      { orderId, transferOutAfterLink }
    );
    this.selectedPrescriptionToLinkId = null;
    this.showLinkPrescriptionModal = false;
    this.enableTransferOutAfterLink = false;
  }

  openLinkPrescriptionModal(enableTransferOutAfterLink: boolean = false) {
    this.enableTransferOutAfterLink = enableTransferOutAfterLink;
    this.selectedPrescriptionToLinkId = null;
    this.showLinkPrescriptionModal = true;
  }

  closeLinkPrescriptionModal() {
    this.showLinkPrescriptionModal = false;
    this.enableTransferOutAfterLink = false;
    this.selectedPrescriptionToLinkId = null;
  }

  onLinkPrescriptionModalVisibleChange(visible: boolean) {
    if (!visible) {
      this.closeLinkPrescriptionModal();
    }
  }

  onTransferToPharmacyClick() {
    if (!this.orderId || !this.order || this.isTransferOut) {
      return;
    }

    const linkedRx = this.linkedPrescription;
    if (linkedRx) {
      this.modalService.confirm({
        nzTitle: 'Enable transfer out?',
        nzContent: `Order #${this.orderId} will be marked as transfer out and linked RX ${linkedRx._id} will be marked transferred out.`,
        nzOkText: 'Enable',
        nzCancelText: 'Cancel',
        nzOnOk: () => this.orderFacade.updateOrder(this.orderId, { transferOut: true })
      });
      return;
    }

    this.modalService.confirm({
      nzTitle: 'Link prescription first?',
      nzContent:
        this.availablePrescriptionsToLink.length > 0
          ? 'No RX is linked to this order. Do you want to link a prescription before enabling transfer out?'
          : 'No linkable RX is available. Continue enabling transfer out anyway?',
      nzOkText:
        this.availablePrescriptionsToLink.length > 0
          ? 'Link Prescription'
          : 'Enable Transfer Out',
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        if (this.availablePrescriptionsToLink.length > 0) {
          // Let the confirm modal close first, then open selector modal.
          setTimeout(() => this.openLinkPrescriptionModal(true), 0);
          return true;
        }
        this.orderFacade.updateOrder(this.orderId, { transferOut: true });
        return true;
      }
    });
  }

  disableTransferOut() {
    if (!this.orderId || !this.order || !this.isTransferOut) {
      return;
    }
    this.modalService.confirm({
      nzTitle: 'Ship order instead?',
      nzContent:
        'This only updates the order flag. Any RX already marked as transferred out will remain transferred out.',
      nzOkText: 'Ship Order Instead',
      nzCancelText: 'Cancel',
      nzOnOk: () => this.orderFacade.updateOrder(this.orderId, { transferOut: false })
    });
  }

  editRequirements() {
    this.isRequirementsEditMode = true;
    this.selectedReq = null;
  }

  copyToBin(value?: string | number | object) {

    const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
    this.clipboard.copy(text);
    this.notification.success('Copied to clipboard', '');
    return;
  }

  /**
   * DIN → drug name, so per-drug pharmacy state can be read by a human. Built from
   * the order's own prescriptions; a DIN we cannot resolve falls back to itself
   * rather than being dropped.
   */
  private drugNamesByDin = new Map<string, string>();

  private syncDrugNamesByDin(): void {
    this.drugNamesByDin = new Map();
    for (const rx of this.order?.rxResults?.prescriptions ?? []) {
      for (const drug of rx?.drugs ?? []) {
        const name = [drug?.drugName, drug?.strength].filter(Boolean).join(' ');
        if (drug?.din && name) this.drugNamesByDin.set(drug.din, name);
      }
    }
  }

  /** Entries per DIN are a list; older data holds a single object. */
  private displayPharmacyOrders(value: Record<string, unknown>): string {
    return Object.entries(value)
      .flatMap(([din, raw]) =>
        (Array.isArray(raw) ? raw : [raw]).map(item => [din, item] as const)
      )
      .map(([din, raw]) => {
        const entry = (raw ?? {}) as Record<string, unknown>;
        const label =
          (entry.itemName as string) || this.drugNamesByDin.get(din) || din;
        const tracking = [entry.carrier, entry.trackingNumber]
          .filter(Boolean)
          .join(' ');
        const detail = [entry.status, tracking].filter(Boolean).join(', ');
        // No status yet: the id is the only thing worth showing.
        return `${label} — ${detail || entry.pharmacyOrderId || 'submitted'}`;
      })
      .join('; ');
  }

  displayExtra(key: string, value: unknown): string {
    if (key === 'pharmacyOrders' && value && typeof value === 'object') {
      return this.displayPharmacyOrders(value as Record<string, unknown>);
    }
    if (key === 'pharmacyAddress' && value && typeof value === 'object') {
      const address = value as IPharmacyAddress;
      return [
        address.name,
        address.address1,
        address.address2,
        address.city,
        address.provinceCode,
        address.postalCode
      ]
        .filter(Boolean)
        .join(', ');
    }
    return value as string;
  }

  copyCurrentUrlToClipboard() {
    this.clipboard.copy(window.location.href);
    this.notification.success('URL copied to clipboard', '');
  }

  addRequirements() {
    const addReqs = this.selectedReq.map(req => ({
      type: req.type,
      key: req.key,
      label: req.name,
      status:
        req.type === 'placeholder' ? 'satisfied' : 'not_satisfied',
      source: 'manual'
    }));
    this.additionalReqs.push(...addReqs);
    this.saveRequirements();
  }

  removeRequirement(index: number) {
    this.additionalReqs.splice(index, 1);
  }

  saveRequirements() {
    this.consultationFacade
      .getSatisfiedConsultations(this.orderCustomer._id)
      .then(res => {
        const reqs = [...this.requirements, ...this.additionalReqs];
        const consultSatisfied = (req: IOrderRequirement) =>
          req.type === 'placeholder' || !!res[req.key];
        let newStatus;
        if (reqs.every(consultSatisfied)) {
          newStatus =
            this.order.status === 'awaiting_requirements'
              ? 'processing'
              : this.order.status;
        } else {
          newStatus = 'awaiting_requirements';
        }

        this.orderFacade.updateOrder(this.orderId, {
          status: newStatus,
          requirements: this.additionalReqs
        });
        this.isRequirementsEditMode = false;
        this.selectedReq = null;
      })
      .finally(() => {
        this.isRequirementsEditMode = false;
        this.selectedReq = null;
      });
  }

  openAllConsultationsView() {
    this.drawerFacade.openDrawer({
      name: DrawerType.VIEW_ALL_CONSULTATIONS,
      props: {
        requirements: [...this.requirements, ...this.additionalReqs],
        customer: this.orderCustomer,
        orderId: this.orderId
      },
      onClose: () => this.loadOrderRequirements()
    });
  }

  private syncOrderExtrasView(): void {
    const extras = this.order?.extras;
    this.filteredOrderExtrasEntries = extras
      ? Object.entries(extras).map(([key, value]) => ({ key, value }))
      : [];
    this.syncDrugNamesByDin();
    this.syncProductTableRows();
    this.syncStatusTimelineEntries();
  }

  private syncProductTableRows(): void {
    const rows: OrderDetailsComponent['productTableRows'] = [];
    for (const lineItem of this.order?.lineItems ?? []) {
      const raw = lineItem.providerPrescribingContext;
      let prescribingContext: IProviderPrescribingContext | null = null;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const ctx: IProviderPrescribingContext = {
          ...(raw.title ? { title: raw.title } : {}),
          ...(raw.version != null ? { version: raw.version } : {}),
          items: Array.isArray(raw.items) ? raw.items : [],
          indicators: Array.isArray(raw.indicators) ? raw.indicators : []
        };
        prescribingContext =
          ctx.title || ctx.items.length || ctx.indicators.length ? ctx : null;
      }
      rows.push({ lineItem, prescribingContext });
    }
    this.productTableRows = rows;
  }

  editExtras() {
    this.extrasUpdates = Object.entries(this.order.extras || {});
    this.isExtrasEditMode = true;
  }

  removeExtraField(field: Array<string>) {
    this.extrasUpdates.findIndex((item, index) => {
      if (item[0] === field[0]) {
        this.extrasUpdates[index][1] = null;
        return true;
      }
    });
  }

  addExtraField() {
    this.extrasUpdates.push(['', '']);
  }

  saveExtras() {
    const extraUpdatesJson = Object.fromEntries(this.extrasUpdates);
    this.orderFacade.updateOrder(this.orderId, { extras: extraUpdatesJson });
    this.isExtrasEditMode = false;
  }

  saveNote() {
    this.orderFacade.updateOrder(this.orderId, { note: this.noteInput });
  }

  get isJjc(): boolean {
    return this.selectedLayout?.isJjc ?? false;
  }

  saveAssignedToGroup(val: string) {
    this.orderFacade.updateOrder(this.orderId, { assignedToGroup: val });
  }

  saveAssignedTo(val: string | null) {
    this.orderFacade.updateOrder(this.orderId, {
      assignedTo: val || null,
    });
  }

  saveAssignedPharmacy(val: string | null) {
    const selected = this.pharmaciesOptions.find(p => p._id === val);
    // Legacy pharmacies are crm-users and live on assignedTo; new (collection)
    // pharmacies live on assignedPharmacy. Route to the correct field so both
    // remain resolvable, and clear the other pharmacy field.
    if (selected?.source === 'legacy') {
      this.orderFacade.updateOrder(this.orderId, {
        assignedTo: val || null,
        assignedPharmacy: null,
      });
    } else {
      this.orderFacade.updateOrder(this.orderId, {
        assignedPharmacy: val || null,
      });
    }
  }

  assignToMe() {
    this.orderFacade.updateOrder(this.orderId, {
      assignedTo: this.currentUser._id
    });
  }

  saveStatus(status: OrderStatusKey) {
    if (status === 'rejected') {
      this.modalService.create<OrderedRejectedDialogComponent>({
        nzTitle: 'Order Rejected',
        nzContent: OrderedRejectedDialogComponent,
        nzData: { order: this.order },
        nzFooter: null
      });
    } else if (status === 'cancelled') {
      this.modalService.create<OrderedCancelledDialogComponent>({
        nzTitle: 'Order Cancelled',
        nzContent: OrderedCancelledDialogComponent,
        nzData: { order: this.order },
        nzFooter: null
      });
    } else if (status === 'shipped') {
      this.modalService.create<OrderedShippedDialogComponent>({
        nzTitle: 'Ordered Shipped',
        nzContent: OrderedShippedDialogComponent,
        nzData: {
          order: this.order,
          postalCode: this.order.shippingAddress.postalCode
        },
        nzFooter: null
      });
    } else {
      this.orderFacade.updateOrder(this.orderId, { status });
    }
  }

  retriggerOrderStatus() {
    this.modalService.confirm({
      nzTitle: 'Retrigger order status',
      nzContent: 'Are you sure?',
      nzOkText: 'Yes',
      nzCancelText: 'Cancel',
      nzOnOk: () => {
        this.orderFacade.retriggerOrderStatus(this.order.id);
        this.retriggerStatusAvailable = false;
      }
    });
  }
  openPaymentStatusModal() {
    if (!this.order) return;
    this.showPaymentStatusModal = true;
  }

  openBigCommerce(id: string) {
    this.orderFacade.openBigcommerce(id);
  }

  openStripe(id: string) {
    this.orderFacade.openStripe(id);
  }

  openTrackingLink() {
    this.orderFacade.openTrackingLink(
      this.order.trackingLink ||
      getTrackingLink(this.order.trackingCode, this.order.carrier)
    );
  }

  openShipstation(id: string) {
    this.orderFacade.openShipstation(id);
  }

  openRefillDrawer(product) {
    this.drawerFacade.openDrawer({
      name: DrawerType.ORDER_CREATE,
      props: {
        products: [product],
        customerId: this.orderCustomer._id,
        mode: 'refill'
      }
    } as DrawerParms);
  }

  getInvoicesIds() {
    return this.invoices?.map(invoice => invoice.id).join(', ');
  }

  saveAddress() {
    for (const i in this.addressFG.controls) {
      this.addressFG.controls[i].markAsDirty();
      this.addressFG.controls[i].updateValueAndValidity();
    }

    if (this.addressFG.valid) {
      const value = this.addressFG.value;
      this.orderFacade.updateOrder(this.orderId, { shippingAddress: value });
      this.isShippingAddressEditMode = false;
    }
  }

  enableEditAddress() {
    this.addressFG.patchValue(this.order.shippingAddress);
    this.isShippingAddressEditMode = true;
  }

  disableEditAddress() {
    this.addressFG.reset();
    this.isShippingAddressEditMode = false;
  }

  enableEditPharmacyAddress() {
    this.pharmacyAddressFG.patchValue({
      name: this.pharmacyAddress?.name || '',
      phoneNumber: this.pharmacyAddress?.phoneNumber || '',
      faxNumber: this.pharmacyAddress?.faxNumber || '',
      address1: this.pharmacyAddress?.address1 || '',
      address2: this.pharmacyAddress?.address2 || '',
      city: this.pharmacyAddress?.city || '',
      provinceCode: this.pharmacyAddress?.provinceCode || '',
      postalCode: this.pharmacyAddress?.postalCode || '',
      countryCode: this.pharmacyAddress?.countryCode || 'CA'
    });
    this.isPharmacyAddressEditMode = true;
  }

  disableEditPharmacyAddress() {
    this.pharmacyAddressFG.reset();
    this.isPharmacyAddressEditMode = false;
  }

  savePharmacyAddress() {
    for (const i in this.pharmacyAddressFG.controls) {
      this.pharmacyAddressFG.controls[i].markAsDirty();
      this.pharmacyAddressFG.controls[i].updateValueAndValidity();
    }
    if (!this.pharmacyAddressFG.valid) {
      this.notification.error(
        'Invalid pharmacy address',
        'Please fix the highlighted fields before saving.'
      );
      return;
    }
    const value = this.pharmacyAddressFG.value as IPharmacyAddress;
    this.orderFacade.updateOrder(this.orderId, { pharmacyAddress: value });
    this.isPharmacyAddressEditMode = false;
  }

  onRequirementClick(req: IOrderRequirement) {
    if (req.type === 'placeholder') {
      return;
    }
    if (req.type === 'lab_test') {
      const labResultId = req.extras?.labResultId;

      if (!labResultId) {
        this.notification.error('Error', 'Lab result ID is missing');
        return;
      }

      this.labResultsFacade.openLabResultPdf(labResultId);

      return;
    }
    if (req.type === 'verification') {
      const documentType = VERIFICATION_REQS[
        req.key as keyof typeof VERIFICATION_REQS
      ]?.documentType as 'drugCard' | 'govId' | undefined;
      if (!documentType) {
        // Verification key with no document mapping — no-op rather than guess
        return;
      }
      this.customerSummaryRef?.openIdImages(documentType);
      return;
    }
    const qnr = req.qnr;
    if (!qnr) {
      // No consultation exists yet for this questionnaire (never started)
      return;
    }
    this.consultationFacade.setCurrentConsultation(qnr._id);
    this.drawerFacade.openDrawer({
      name: DrawerType.OPEN_CONSULTATION,
      props: { consultation: qnr },
      onClose: () => this.loadOrderRequirements()
    });
  }

  openPrescriptionsListDrawer() {
    if (!this.orderCustomer) {
      return;
    }
    this.drawerFacade.openDrawer({
      name: DrawerType.CUSTOMER_PRESCRIPTIONS,
      props: { customer: this.orderCustomer }
    });
  }

  openCreateRx(sendRx: boolean = true) {
    if (
      this.selectedLayout?.dosespotUiUrl &&
      this.orderCustomer?.['extras']?.dosespotId
    ) {
      this.dosespotService.openDosespot({
        patientId: this.orderCustomer.extras.dosespotId,
        orderId: this.orderId.toString()
      });
      return;
    }

    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_RX,
      props: {
        customerId: this.orderCustomer._id,
        orderId: this.orderId,
        sendRx: sendRx
      }
    });
  }

  openCreateLabReq() {
    if (!this.selectedLabReqTemplate) return;
    this.labReqFacade.clearActiveLabReq();
    const customerId = typeof this.order.customer === 'string'
      ? this.order.customer
      : this.order.customer?._id;
    if (customerId) this.customerFacade.loadCustomer(customerId);
    if (this.selectedLabReqTemplate.v === 2) {
      this.drawerFacade.openDrawer({
        name: DrawerType.LAB_REQ_DETAIL_V2,
        props: { template: this.selectedLabReqTemplate, mode: 'create' }
      });
    } else {
      this.drawerFacade.openDrawer({
        name: DrawerType.LAB_REQ_DETAIL,
        props: { templateId: this.selectedLabReqTemplate.id, mode: 'create' }
      });
    }
  }

  openViewLabReq(labReq: ILabReq) {
    this.labReqFacade.loadLabReqPdfUrl(labReq._id);
  }

  confirmMarkCompleted() {
    this.orderFacade.updateOrder(this.orderId, { status: 'completed' });
    this.showMarkCompletedModal = false;
  }

  switchOrder(direction: OrderDirection) {
    this.orderFacade.switchOrder(direction);
  }

  loadAvailableRequirements() {
    if (!this.selectedLayout) {
      console.error('Selected Layout is undefined');
      return;
    }
    this.availableQs = getAvailableRequirements(this.selectedLayout);
  }

  // Product editor event handlers
  onProductsSave(updatedOrder: Partial<IOrder>) {
    const originalTotal = this.order?.totalAmount || 0;
    const newTotal = updatedOrder.totalAmount;

    // Open modal if total amount has changed
    if (newTotal !== originalTotal) {
      this.editedOrder = updatedOrder;
      this.showTotalAmountModal = true;
      return;
    }

    this.confirmProductsUpdate(updatedOrder);
  }

  toggleProductsEditMode() {
    this.isProductsEditMode = !this.isProductsEditMode;
  }

  cancelProductsEdit() {
    if (this.productEditor) {
      this.productEditor.cancelEdit();
    }
    this.isProductsEditMode = false;
  }

  onFormValidityChange(validity: boolean) {
    this.formValidity = validity;
  }

  @ViewChild('productEditor', { static: false })
  productEditor: OrderEditorComponent;

  @ViewChild('customerSummaryRef', { static: false })
  customerSummaryRef: CustomerSummaryComponent;

  saveProductsEdit() {
    if (this.productEditor) {
      this.productEditor.saveChanges();
    }
  }

  confirmSave() {
    this.confirmProductsUpdate(this.editedOrder);
  }

  confirmProductsUpdate(updatedOrder: Partial<IOrder>) {
    const newTotal = updatedOrder.totalAmount;
    const payment = this.order?.payment;

    // Keep deferred balance in sync when EMR adjusts pricing/insurance so
    // confirm dialogs and payment.delayedAmount match what chargeOrder uses.
    if (payment && typeof newTotal === 'number') {
      const delayedAmount = resolveDeferredAmountAfterTotalChange(
        payment,
        newTotal
      );
      if (
        delayedAmount !== undefined &&
        delayedAmount !== payment.delayedAmount
      ) {
        updatedOrder = {
          ...updatedOrder,
          payment: {
            ...payment,
            delayedAmount
          }
        };
      }
    }

    this.orderFacade.updateOrder(this.orderId, updatedOrder);
    this.isProductsEditMode = false;
    this.showTotalAmountModal = false;
  }

  /**
   * Amount already captured from the patient.
   * Pending/deferred statuses may store an intended amount on payment.amount —
   * only treat it as charged once payment has actually succeeded (or partial).
   */
  get patientChargedAmount(): number {
    const status = this.order?.payment?.status;
    if (
      status === 'partial_paid' ||
      status === 'paid' ||
      status === 'partially_refunded'
    ) {
      return this.order?.payment?.amount || 0;
    }
    return 0;
  }

  /** Remaining amount that Charge Order will collect. */
  get patientDueAmount(): number {
    const status = this.order?.payment?.status;
    if (
      status === 'paid' ||
      status === 'skipped' ||
      status === 'refunded' ||
      status === 'partially_refunded'
    ) {
      return 0;
    }
    return resolveOrderChargeAmount(this.order || {});
  }

  chargeOrder() {
    if (!this.order || !this.orderCustomer?._id) {
      return;
    }

    const orderAmount = resolveOrderChargeAmount(this.order);
    const formattedAmount = '$' + parseFloat('' + orderAmount).toFixed(2);
    const customerName =
      (this.orderCustomer.firstName || '') +
      ' ' +
      (this.orderCustomer.lastName || '');

    this.modalService.confirm({
      nzTitle: 'Confirm Charge Order',
      nzContent: `
        <div>
          <p>Are you sure you want to charge this order?</p>
          <p class="mt-2"><strong>Order #${this.orderId}</strong></p>
          <p><strong>Customer:</strong> ${customerName.trim() || 'N/A'}</p>
          <p><strong>Amount:</strong> ${formattedAmount}</p>
        </div>
      `,
      nzOkText: 'Charge Order',
      nzOkType: 'primary',
      nzOkDanger: false,
      nzCancelText: 'Cancel',
      nzOnOk: async () => {
        this.isLoading = true;
        try {
          await this.orderFacade.chargeOrder(
            String(this.order.id),
            this.orderCustomer._id
          );
        } finally {
          this.isLoading = false;
        }
      }
    });
  }

  getReqTooltipTitle(req: IOrderRequirement) {
    if (req.type === 'placeholder') {
      return req?.description || req.label;
    }
    if (req.type !== 'lab_test') {
      return req.qnr ? this.qnrStatusNames[req.qnr.status] : 'No Consult';
    } else {
      return req.status === 'not_satisfied' ? 'No Lab Result' : 'View Lab Result';
    }
  }

  openSubscriptionDrawer(subscriptionId: string) {
    this.subscriptionsFacade.openSubscriptionDetailsDrawer(subscriptionId);
  }

  onBelugaVisitCanceled() {
    this.orderFacade.loadOrder(this.orderId);
  }
}
